/**
 * Logger Utility with Date-wise Log Organization
 * 
 * This logger provides structured logging with:
 * - Multiple log levels (debug, info, warn, error)
 * - Timestamps and component identification
 * - LocalStorage-based date-wise log persistence
 * - Console output with formatting
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  component: string;
  message: string;
  data?: any;
  userId?: number;
  requestId?: number;
}

class Logger {
  private static instance: Logger;
  private readonly LOG_PREFIX = '[7Hills]';
  private readonly STORAGE_KEY = '7hills_logs';
  private readonly MAX_LOGS_PER_DAY = 1000;
  private readonly MAX_DAYS_TO_KEEP = 30;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private getDateString(): string {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  private getTimestamp(): string {
    return new Date().toISOString();
  }

  private getConsoleMethod(level: LogLevel) {
    switch (level) {
      case 'debug':
        return console.debug;
      case 'info':
        return console.info;
      case 'warn':
        return console.warn;
      case 'error':
        return console.error;
      default:
        return console.log;
    }
  }

  private getLevelColor(level: LogLevel): string {
    switch (level) {
      case 'debug':
        return '#9CA3AF';
      case 'info':
        return '#3B82F6';
      case 'warn':
        return '#F59E0B';
      case 'error':
        return '#EF4444';
      default:
        return '#000000';
    }
  }

  private formatConsoleMessage(entry: LogEntry): string {
    const color = this.getLevelColor(entry.level);
    const timestamp = entry.timestamp.split('T')[1].split('.')[0];
    return `${this.LOG_PREFIX} [${timestamp}] [${entry.level.toUpperCase()}] [${entry.component}] ${entry.message}`;
  }

  private writeToLocalStorage(entry: LogEntry): void {
    try {
      const dateString = this.getDateString();
      const storageKey = `${this.STORAGE_KEY}_${dateString}`;
      
      const existingLogs = localStorage.getItem(storageKey);
      const logs: LogEntry[] = existingLogs ? JSON.parse(existingLogs) : [];
      
      // Add new log entry
      logs.push(entry);
      
      // Limit logs per day
      if (logs.length > this.MAX_LOGS_PER_DAY) {
        logs.shift(); // Remove oldest
      }
      
      localStorage.setItem(storageKey, JSON.stringify(logs));
      
      // Clean up old logs
      this.cleanOldLogs();
    } catch (error) {
      console.error('Failed to write logs to localStorage:', error);
    }
  }

  private cleanOldLogs(): void {
    try {
      const keys = Object.keys(localStorage);
      const today = new Date();
      
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_KEY)) {
          const dateString = key.split('_').slice(2).join('_');
          const logDate = new Date(dateString);
          const daysDiff = Math.floor((today.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDiff > this.MAX_DAYS_TO_KEEP) {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Failed to clean old logs:', error);
    }
  }

  private log(level: LogLevel, component: string, message: string, data?: any, userId?: number, requestId?: number): void {
    const entry: LogEntry = {
      timestamp: this.getTimestamp(),
      level,
      component,
      message,
      data,
      userId,
      requestId,
    };

    // Console output
    const consoleMethod = this.getConsoleMethod(level);
    const formattedMessage = this.formatConsoleMessage(entry);
    
    if (data) {
      consoleMethod(formattedMessage, data);
    } else {
      consoleMethod(formattedMessage);
    }

    // LocalStorage persistence
    this.writeToLocalStorage(entry);
  }

  /**
   * Log debug message
   */
  debug(component: string, message: string, data?: any, userId?: number, requestId?: number): void {
    this.log('debug', component, message, data, userId, requestId);
  }

  /**
   * Log info message
   */
  info(component: string, message: string, data?: any, userId?: number, requestId?: number): void {
    this.log('info', component, message, data, userId, requestId);
  }

  /**
   * Log warning message
   */
  warn(component: string, message: string, data?: any, userId?: number, requestId?: number): void {
    this.log('warn', component, message, data, userId, requestId);
  }

  /**
   * Log error message
   */
  error(component: string, message: string, data?: any, userId?: number, requestId?: number): void {
    this.log('error', component, message, data, userId, requestId);
  }

  /**
   * Get logs for a specific date
   */
  getLogsByDate(dateString: string): LogEntry[] {
    try {
      const storageKey = `${this.STORAGE_KEY}_${dateString}`;
      const logs = localStorage.getItem(storageKey);
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('Failed to retrieve logs:', error);
      return [];
    }
  }

  /**
   * Get all available log dates
   */
  getAvailableLogDates(): string[] {
    try {
      const keys = Object.keys(localStorage);
      return keys
        .filter(key => key.startsWith(this.STORAGE_KEY))
        .map(key => key.split('_').slice(2).join('_'))
        .sort()
        .reverse();
    } catch (error) {
      console.error('Failed to get log dates:', error);
      return [];
    }
  }

  /**
   * Export logs for a date range as JSON
   */
  exportLogs(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allLogs: LogEntry[] = [];
    
    const current = new Date(start);
    while (current <= end) {
      const dateString = current.toISOString().split('T')[0];
      const logs = this.getLogsByDate(dateString);
      allLogs.push(...logs);
      current.setDate(current.getDate() + 1);
    }
    
    return JSON.stringify(allLogs, null, 2);
  }

  /**
   * Clear all logs
   */
  clearAllLogs(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_KEY)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions for quick logging
export const logDebug = (component: string, message: string, data?: any, userId?: number, requestId?: number) => 
  logger.debug(component, message, data, userId, requestId);

export const logInfo = (component: string, message: string, data?: any, userId?: number, requestId?: number) => 
  logger.info(component, message, data, userId, requestId);

export const logWarn = (component: string, message: string, data?: any, userId?: number, requestId?: number) => 
  logger.warn(component, message, data, userId, requestId);

export const logError = (component: string, message: string, data?: any, userId?: number, requestId?: number) => 
  logger.error(component, message, data, userId, requestId);
